import { toConfidenceScore, type ConfidenceScore } from '../../../types/branded';
import { type SmartMatchResult, type KalenderEintrag, type MatchKategorie } from '../types';
import { MATCH_REGELN } from './matchingRegeln';

// ── Hilfsfunktionen ────────────────────────────────────────────────────────

function buildSuchtext(eintrag: KalenderEintrag): string {
  return [
    eintrag.titel,
    eintrag.beschreibung ?? '',
    eintrag.ort ?? '',
  ].join(' ').toLowerCase();
}

// Einfacher Titelvergleich: Score 0–100 basierend auf Token-Overlap
function tokenSimilarity(a: string, b: string): number {
  const tokA = new Set(a.toLowerCase().split(/\W+/).filter(t => t.length > 2));
  const tokB = new Set(b.toLowerCase().split(/\W+/).filter(t => t.length > 2));
  if (tokA.size === 0 || tokB.size === 0) { return 0; }
  let overlap = 0;
  for (const t of tokA) { if (tokB.has(t)) { overlap++; } }
  return Math.round((overlap * 2 / (tokA.size + tokB.size)) * 100);
}

// ── Kern-Match ────────────────────────────────────────────────────────────

export function matchEintrag(eintrag: KalenderEintrag): SmartMatchResult {
  const suchtext = buildSuchtext(eintrag);

  // 1. Regelbasiertes Matching
  const treffer = MATCH_REGELN
    .filter(r => r.pattern.test(suchtext))
    .sort((a, b) => b.confidence - a.confidence);

  if (treffer.length > 0) {
    const best = treffer[0]!;
    const alternativenCodes = treffer
      .slice(1, 4)
      .map(r => r.leistungCode)
      .filter(c => c !== best.leistungCode);

    return {
      kategorie:       best.kategorie,
      leistungCode:    best.leistungCode,
      tatbestandCode:  best.tatbestandCode,
      confidence:      best.confidence,
      grund:           best.grund,
      alternativenCodes,
    };
  }

  // 2. Fallback: Token-Similarity gegen häufige Begriffe
  const fallbackMap: Array<{ terms: string[]; kategorie: MatchKategorie; code: string }> = [
    { terms: ['vgü', 'vgu', 'vorsorge', 'untersuchung'],        kategorie: 'VGÜ',      code: 'VGÜ-02' },
    { terms: ['begehung', 'rundgang', 'betrieb'],                kategorie: 'Begehung', code: 'BEG-01' },
    { terms: ['asa', 'ausschuss', 'sitzung'],                    kategorie: 'ASA',      code: 'BEG-02' },
    { terms: ['schulung', 'kurs', 'training', 'unterweisung'],   kategorie: 'Schulung', code: 'BER-03' },
    { terms: ['beratung', 'gespräch', 'konsultation'],           kategorie: 'Beratung', code: 'BER-01' },
  ];

  let bestFallback = { score: 0, kategorie: 'Unbekannt' as MatchKategorie, code: '' };
  for (const fb of fallbackMap) {
    const score = tokenSimilarity(suchtext, fb.terms.join(' '));
    if (score > bestFallback.score) {
      bestFallback = { score, kategorie: fb.kategorie, code: fb.code };
    }
  }

  if (bestFallback.score >= 30) {
    return {
      kategorie:        bestFallback.kategorie,
      leistungCode:     bestFallback.code,
      confidence:       toConfidenceScore(Math.min(65, bestFallback.score)) as ConfidenceScore,
      grund:            `Fallback via Token-Ähnlichkeit (Score ${bestFallback.score}%)`,
      alternativenCodes: [],
    };
  }

  // 3. Kein Match
  return {
    kategorie:        'Unbekannt',
    leistungCode:     '',
    confidence:       toConfidenceScore(0),
    grund:            'Kein passendes Muster gefunden',
    alternativenCodes: [],
  };
}

// ── Batch-Matching ────────────────────────────────────────────────────────

export function matchBatch(eintraege: KalenderEintrag[]): Map<string, SmartMatchResult> {
  const results = new Map<string, SmartMatchResult>();
  for (const e of eintraege) {
    results.set(e.id, matchEintrag(e));
  }
  return results;
}

// ── Confidence-Label ──────────────────────────────────────────────────────

export function confidenceLabel(score: ConfidenceScore): { text: string; color: string } {
  if (score >= 85) { return { text: 'Hoch',    color: 'text-green-700 bg-green-100' }; }
  if (score >= 65) { return { text: 'Mittel',  color: 'text-yellow-700 bg-yellow-100' }; }
  if (score >= 35) { return { text: 'Niedrig', color: 'text-orange-700 bg-orange-100' }; }
  return             { text: 'Kein Match', color: 'text-gray-500 bg-gray-100' };
}
